/**
 * Argus Observer Page
 *
 * Vision-Native Observation Agent dashboard for visual regression testing,
 * runtime monitoring, and workforce feedback.
 *
 * Part of Project Argus
 */
import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  EyeIcon,
  PlayIcon,
  StopIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  PhotoIcon,
  DocumentMagnifyingGlassIcon,
  BoltIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { rawApi } from '@/services/api'
import toast from 'react-hot-toast'

// Types
interface ArgusSession {
  session_id: string
  browser_id: string
  target_url: string
  agent_id?: string
  workspace_id?: string
  created_at: string
  last_audit_at?: string
  audit_count: number
  drips_sent: number
  is_monitoring: boolean
  auto_audit: boolean
}

interface AuditResult {
  session_id: string
  page_url: string
  status: 'PASS' | 'FAIL' | 'WARNING' | 'ERROR'
  audit_time_ms: number
  drip: any
  visual_comparison?: any
  console_logs_count: number
  network_errors_count: number
}

interface ArgusStats {
  initialized: boolean
  active_sessions: number
  total_audits: number
  total_regressions: number
  total_drips: number
  sessions: any[]
}

interface StorageStats {
  total_screenshots: number
  total_size_bytes: number
  total_size_mb: number
  baselines_count: number
  oldest_screenshot?: string
  newest_screenshot?: string
  screenshots_by_type: Record<string, number>
}

interface Baseline {
  url_hash: string
  page_url: string
  current_version: number
  versions: any[]
  created_at: string
  updated_at: string
}

// API functions
const fetchArgusStats = async (): Promise<ArgusStats> => {
  const response = await rawApi.get('/api/v1/argus/stats')
  return response.data
}

const fetchStorageStats = async (): Promise<StorageStats> => {
  const response = await rawApi.get('/api/v1/argus/storage/stats')
  return response.data
}

const fetchSessions = async (): Promise<{ sessions: ArgusSession[]; total: number }> => {
  const response = await rawApi.get('/api/v1/argus/sessions')
  return response.data
}

const fetchBaselines = async (): Promise<{ baselines: Baseline[]; total: number }> => {
  const response = await rawApi.get('/api/v1/argus/baselines')
  return response.data
}

const startSession = async (data: {
  url: string
  auto_audit?: boolean
  auto_audit_interval_ms?: number
}) => {
  const response = await rawApi.post('/api/v1/argus/sessions', data)
  return response.data
}

const stopSession = async (sessionId: string) => {
  const response = await rawApi.delete(`/api/v1/argus/sessions/${sessionId}`)
  return response.data
}

const performAudit = async (sessionId: string, context?: string): Promise<AuditResult> => {
  const response = await rawApi.post(`/api/v1/argus/sessions/${sessionId}/audit`, {
    context: context || '',
  })
  return response.data
}

const updateBaseline = async (sessionId: string) => {
  const response = await rawApi.post(`/api/v1/argus/sessions/${sessionId}/baseline`, {})
  return response.data
}

const quickAudit = async (url: string, context?: string) => {
  const response = await rawApi.post('/api/v1/argus/quick-audit', null, {
    params: { url, context: context || '' },
  })
  return response.data
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'success' | 'error' | 'warning' | 'gray'> = {
    PASS: 'success',
    FAIL: 'error',
    WARNING: 'warning',
    ERROR: 'error',
    active: 'success',
    closed: 'gray',
  }
  return <Badge variant={variants[status] || 'gray'}>{status}</Badge>
}

// Stats card component
function StatsCard({
  icon: Icon,
  title,
  value,
  subtitle,
  color = 'primary',
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  value: string | number
  subtitle?: string
  color?: 'primary' | 'green' | 'yellow' | 'red'
}) {
  const colors = {
    primary: 'bg-primary-500/15 text-primary-600',
    green: 'bg-green-500/15 text-green-600',
    yellow: 'bg-yellow-500/15 text-yellow-600',
    red: 'bg-red-500/15 text-red-600',
  }

  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-xl font-semibold text-foreground">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
    </Card>
  )
}

export default function ArgusObserverPage() {
  const queryClient = useQueryClient()
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [newSessionUrl, setNewSessionUrl] = useState('')
  const [autoAudit, setAutoAudit] = useState(false)
  const [auditContext, setAuditContext] = useState('')
  const [quickAuditUrl, setQuickAuditUrl] = useState('')
  const [lastAuditResult, setLastAuditResult] = useState<AuditResult | null>(null)

  // Queries
  const { data: stats } = useQuery({
    queryKey: ['argus-stats'],
    queryFn: fetchArgusStats,
    refetchInterval: 5000,
  })

  const { data: storageStats } = useQuery({
    queryKey: ['argus-storage-stats'],
    queryFn: fetchStorageStats,
    refetchInterval: 30000,
  })

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['argus-sessions'],
    queryFn: fetchSessions,
    refetchInterval: 5000,
  })

  const { data: baselinesData } = useQuery({
    queryKey: ['argus-baselines'],
    queryFn: fetchBaselines,
    refetchInterval: 30000,
  })

  // Mutations
  const startSessionMutation = useMutation({
    mutationFn: startSession,
    onSuccess: (data) => {
      toast.success(`Session started: ${data.session_id}`)
      queryClient.invalidateQueries({ queryKey: ['argus-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['argus-stats'] })
      setNewSessionUrl('')
      setSelectedSession(data.session_id)
    },
    onError: (error: any) => {
      toast.error(`Failed to start session: ${error.response?.data?.detail || error.message}`)
    },
  })

  const stopSessionMutation = useMutation({
    mutationFn: stopSession,
    onSuccess: () => {
      toast.success('Session stopped')
      queryClient.invalidateQueries({ queryKey: ['argus-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['argus-stats'] })
      setSelectedSession(null)
    },
    onError: (error: any) => {
      toast.error(`Failed to stop session: ${error.response?.data?.detail || error.message}`)
    },
  })

  const auditMutation = useMutation({
    mutationFn: ({ sessionId, context }: { sessionId: string; context?: string }) =>
      performAudit(sessionId, context),
    onSuccess: (data) => {
      setLastAuditResult(data)
      queryClient.invalidateQueries({ queryKey: ['argus-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['argus-stats'] })
      if (data.status === 'PASS') {
        toast.success('Audit passed - no issues found')
      } else if (data.status === 'WARNING') {
        toast(`Audit warning: ${data.drip?.reason}`, { icon: '⚠️' })
      } else if (data.status === 'FAIL') {
        toast.error(`Audit failed: ${data.drip?.reason}`)
      }
    },
    onError: (error: any) => {
      toast.error(`Audit failed: ${error.response?.data?.detail || error.message}`)
    },
  })

  const updateBaselineMutation = useMutation({
    mutationFn: updateBaseline,
    onSuccess: () => {
      toast.success('Baseline updated')
      queryClient.invalidateQueries({ queryKey: ['argus-baselines'] })
    },
    onError: (error: any) => {
      toast.error(`Failed to update baseline: ${error.response?.data?.detail || error.message}`)
    },
  })

  const quickAuditMutation = useMutation({
    mutationFn: ({ url, context }: { url: string; context?: string }) => quickAudit(url, context),
    onSuccess: (data) => {
      setLastAuditResult(data)
      if (data.status === 'PASS') {
        toast.success('Quick audit passed')
      } else {
        toast(`Quick audit: ${data.status}`, { icon: '⚠️' })
      }
    },
    onError: (error: any) => {
      toast.error(`Quick audit failed: ${error.response?.data?.detail || error.message}`)
    },
  })

  const handleStartSession = () => {
    if (!newSessionUrl) {
      toast.error('Please enter a URL')
      return
    }
    startSessionMutation.mutate({
      url: newSessionUrl,
      auto_audit: autoAudit,
      auto_audit_interval_ms: 5000,
    })
  }

  const handleQuickAudit = () => {
    if (!quickAuditUrl) {
      toast.error('Please enter a URL')
      return
    }
    quickAuditMutation.mutate({ url: quickAuditUrl })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-500/15 rounded-lg">
            <EyeIcon className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Argus Observer</h1>
            <p className="text-sm text-muted-foreground">
              Vision-Native Observation Agent for visual regression and runtime monitoring
            </p>
          </div>
        </div>
        <Badge variant={stats?.initialized ? 'success' : 'error'}>
          {stats?.initialized ? 'Online' : 'Offline'}
        </Badge>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          icon={ComputerDesktopIcon}
          title="Active Sessions"
          value={stats?.active_sessions || 0}
          color="primary"
        />
        <StatsCard
          icon={DocumentMagnifyingGlassIcon}
          title="Total Audits"
          value={stats?.total_audits || 0}
          color="green"
        />
        <StatsCard
          icon={ExclamationTriangleIcon}
          title="Regressions Found"
          value={stats?.total_regressions || 0}
          color={stats?.total_regressions ? 'yellow' : 'green'}
        />
        <StatsCard
          icon={PhotoIcon}
          title="Baselines"
          value={storageStats?.baselines_count || 0}
          subtitle={`${storageStats?.total_size_mb?.toFixed(1) || 0} MB storage`}
          color="primary"
        />
      </div>

      {/* Quick Audit Section */}
      <Card>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-yellow-500/15 rounded-lg">
            <BoltIcon className="w-6 h-6 text-yellow-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Quick Audit</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Perform a one-off visual audit without maintaining a session
            </p>
            <div className="flex gap-2">
              <input
                type="url"
                value={quickAuditUrl}
                onChange={(e) => setQuickAuditUrl(e.target.value)}
                placeholder="https://example.com"
                className="flex-1 px-3 py-2 border border-border rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
              <Button
                onClick={handleQuickAudit}
                disabled={quickAuditMutation.isPending}
              >
                {quickAuditMutation.isPending ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CameraIcon className="w-4 h-4 mr-2" />
                )}
                Audit
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions Panel */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <h3 className="font-semibold text-foreground mb-4">Start New Session</h3>
            <div className="space-y-3">
              <input
                type="url"
                value={newSessionUrl}
                onChange={(e) => setNewSessionUrl(e.target.value)}
                placeholder="https://localhost:3000"
                className="w-full px-3 py-2 border border-border rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoAudit}
                  onChange={(e) => setAutoAudit(e.target.checked)}
                  className="rounded border-border text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-muted-foreground">Enable auto-audit (every 5s)</span>
              </label>
              <Button
                onClick={handleStartSession}
                disabled={startSessionMutation.isPending}
                className="w-full"
              >
                {startSessionMutation.isPending ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <PlayIcon className="w-4 h-4 mr-2" />
                )}
                Start Session
              </Button>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-foreground mb-4">Active Sessions</h3>
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : sessionsData?.sessions?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No active sessions</p>
            ) : (
              <div className="space-y-2">
                {sessionsData?.sessions?.map((session) => (
                  <div
                    key={session.session_id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedSession === session.session_id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-border hover:border-border'
                    }`}
                    onClick={() => setSelectedSession(session.session_id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground truncate max-w-[150px]">
                        {new URL(session.target_url).hostname}
                      </span>
                      <Badge variant={session.is_monitoring ? 'success' : 'gray'} size="sm">
                        {session.is_monitoring ? 'Monitoring' : 'Idle'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{session.audit_count} audits</span>
                      <span>{session.drips_sent} drips</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Session Details & Results */}
        <div className="lg:col-span-2 space-y-4">
          {selectedSession ? (
            <>
              {/* Session Controls */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-foreground">Session Controls</h3>
                    <p className="text-sm text-muted-foreground">
                      {sessionsData?.sessions?.find((s) => s.session_id === selectedSession)
                        ?.target_url || 'Unknown URL'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        auditMutation.mutate({ sessionId: selectedSession, context: auditContext })
                      }
                      disabled={auditMutation.isPending}
                    >
                      {auditMutation.isPending ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CameraIcon className="w-4 h-4 mr-2" />
                      )}
                      Run Audit
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => updateBaselineMutation.mutate(selectedSession)}
                      disabled={updateBaselineMutation.isPending}
                    >
                      <PhotoIcon className="w-4 h-4 mr-2" />
                      Update Baseline
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => stopSessionMutation.mutate(selectedSession)}
                      disabled={stopSessionMutation.isPending}
                    >
                      <StopIcon className="w-4 h-4 mr-2" />
                      Stop
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Audit Context (optional)
                  </label>
                  <input
                    type="text"
                    value={auditContext}
                    onChange={(e) => setAuditContext(e.target.value)}
                    placeholder="e.g., Changed navbar styles"
                    className="w-full px-3 py-2 border border-border rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </Card>

              {/* Last Audit Result */}
              {lastAuditResult && (
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-foreground">Last Audit Result</h3>
                    <StatusBadge status={lastAuditResult.status} />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-foreground">
                        {lastAuditResult.audit_time_ms}
                      </p>
                      <p className="text-xs text-muted-foreground">Audit Time (ms)</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-foreground">
                        {lastAuditResult.console_logs_count}
                      </p>
                      <p className="text-xs text-muted-foreground">Console Logs</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-foreground">
                        {lastAuditResult.network_errors_count}
                      </p>
                      <p className="text-xs text-muted-foreground">Network Errors</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-foreground">
                        {lastAuditResult.visual_comparison?.differences?.length || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Visual Diffs</p>
                    </div>
                  </div>

                  {lastAuditResult.drip?.reason && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium text-foreground mb-1">Summary</p>
                      <p className="text-sm text-muted-foreground">{lastAuditResult.drip.reason}</p>
                    </div>
                  )}

                  {lastAuditResult.visual_comparison?.differences?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-foreground mb-2">Visual Differences</p>
                      <div className="space-y-2">
                        {lastAuditResult.visual_comparison.differences.map(
                          (diff: any, idx: number) => (
                            <div
                              key={idx}
                              className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="warning" size="sm">
                                  {diff.type}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  {Math.round(diff.confidence * 100)}% confidence
                                </span>
                              </div>
                              <p className="text-sm text-foreground">{diff.description}</p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </>
          ) : (
            <Card>
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <EyeIcon className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No Session Selected
                </h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  Start a new observation session or select an existing one to monitor pages for
                  visual regressions and runtime errors.
                </p>
              </div>
            </Card>
          )}

          {/* Baselines List */}
          <Card>
            <h3 className="font-semibold text-foreground mb-4">Stored Baselines</h3>
            {baselinesData?.baselines?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No baselines stored yet. Run an audit to create one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        URL
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Version
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {baselinesData?.baselines?.slice(0, 10).map((baseline) => (
                      <tr key={baseline.url_hash} className="hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm text-foreground">
                          <div className="flex items-center gap-2">
                            <GlobeAltIcon className="w-4 h-4 text-muted-foreground" />
                            <span className="truncate max-w-[300px]">{baseline.page_url}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          v{baseline.current_version}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(baseline.updated_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
